// Increment 45a: native (Capacitor) startup glue — status bar, splash screen,
// deep links, and priming the token cache. All no-ops on the web build.
import { isNativeApp, primeNativeToken } from "./platform";
import { handleNativeBack } from "./nativeNavigation";
import { initializeAndroidRestoreSettings } from "./androidRestoreSettings";

const PROD_HOSTS = ["v3.uat.kontrataph.com", "kontrataph.com"];

/** Initialize native integrations. Safe to await on web (returns immediately). */
export async function initNative(): Promise<void> {
  if (!isNativeApp()) return;

  // Restore finite settings before I18nProvider or analytics can consume them.
  await initializeAndroidRestoreSettings();
  // Keep the existing preference-backed token available to the API client.
  await primeNativeToken();

  // Status bar + splash.
  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    // Capacitor's Light style supplies dark icons for the app's light system-bar surface.
    await StatusBar.setStyle({ style: Style.Light });
  } catch { /* plugin optional */ }
  try {
    const { SplashScreen } = await import("@capacitor/splash-screen");
    await SplashScreen.hide();
  } catch { /* plugin optional */ }

  // Deep links: when a shared https link to our host opens the app, route to the
  // in-app path so /s/:id, /j/:id, etc. resolve natively instead of the browser.
  try {
    const { App } = await import("@capacitor/app");
    void App.addListener("backButton", ({ canGoBack }) => {
      handleNativeBack(canGoBack, () => App.exitApp());
    });
    App.addListener("appUrlOpen", (event: { url: string }) => {
      try {
        const u = new URL(event.url);
        if (PROD_HOSTS.includes(u.hostname)) {
          const target = u.pathname + u.search + u.hash;
          if (target && target !== "/") window.history.pushState({}, "", target);
        }
      } catch { /* ignore malformed */ }
    });
  } catch { /* plugin optional */ }
}
